import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AppProvider, useApp } from './context/AppContext';
import Login from './components/Login';
import CheckRef from './components/CheckRef';
import Layout from './components/Layout';
import ReporterDashboard from './pages/reporter/ReporterDashboard';
import ReporterCreatePage from './pages/reporter/ReporterCreatePage';
import ServiceProviderDashboard from './pages/service/ServiceProviderDashboard';
import NotificationsPage from './pages/service/NotificationsPage';
import NotificationFormPage from './pages/service/NotificationFormPage';
import HelpDeskDetail from './pages/shared/HelpDeskDetail';
import TicketDetailPage from './pages/shared/TicketDetailPage';
import MembersManagementPage from './pages/shared/MembersManagementPage';
import MemberFormPage from './pages/shared/MemberFormPage';
import SelectAppPage, { SELECTED_APP_ID_KEY, SELECTED_APP_USER_HAS_ACCESS_KEY } from './pages/shared/SelectAppPage';
import SendNotification from './pages/shared/SendNotification';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#845EC2',
      light: '#a78bd4',
      dark: '#6344A4',
    },
    secondary: {
      main: '#6344A4',
      light: '#845EC2',
      dark: '#4a2f7a',
    },
    background: {
      default: 'rgb(240, 232, 245)',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
  },
  typography: {
    fontFamily: [
      'Kanit',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.6)',
    },
    h5: {
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.6)',
    },
    h6: {
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          borderRadius: 1,
          fontWeight: 500,
          '&.MuiButton-contained': {
            backgroundColor: '#845EC2',
            color: 'white',
            '&:hover': {
              backgroundColor: '#6344A4',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
        },
      },
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) {
    // After login, always go to /select-app when coming from manage-members (don't restore this path)
    const path = location.pathname + location.search;
    const from = path.startsWith('/manage-members') ? undefined : path;
    return <Navigate to="/login" state={from != null ? { from } : {}} replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { currentUser, authLoading } = useApp();
  const location = useLocation();
  const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
  const fromPath = location.state?.from;

  const selectedAppUserHasAccess = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_USER_HAS_ACCESS_KEY) : null;
  const canAccessServiceProvider = selectedAppUserHasAccess === 'true' || currentUser?.is_staff;

  const getDashboard = () => {
    if (selectedAppUserHasAccess === 'true') {
      return <ServiceProviderDashboard />;
    }
    if (selectedAppUserHasAccess === 'false') {
      return <ReporterDashboard />;
    }
    if (currentUser?.is_staff) {
      return <ServiceProviderDashboard />;
    }
    return <ReporterDashboard />;
  };

  return (
    <Routes>
      <Route
        path="/superapps"
        element={<CheckRef />}
      />
      <Route
        path="/send-notification-preview"
        element={<SendNotification />}
      />
      <Route
        path="/login"
        element={
          (() => {
            const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
            if (token) {
              return <Navigate to={fromPath || (selectedAppId ? '/' : '/select-app')} replace />;
            }
            return <Login />;
          })()
        }
      />
      <Route
        path="/select-app"
        element={
          <ProtectedRoute>
            <SelectAppPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporter/create"
        element={
          <ProtectedRoute>
            {selectedAppId ? (
              <Layout><ReporterCreatePage /></Layout>
            ) : (
              <Navigate to="/select-app" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {selectedAppId
              ? <Layout>{getDashboard()}</Layout>
              : <Navigate to="/select-app" replace />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            {selectedAppId && canAccessServiceProvider ? (
              <Layout>
                <NotificationsPage />
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/create"
        element={
          <ProtectedRoute>
            {selectedAppId && canAccessServiceProvider ? (
              <Layout>
                <NotificationFormPage />
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/edit/:id"
        element={
          <ProtectedRoute>
            {selectedAppId && canAccessServiceProvider ? (
              <Layout>
                <NotificationFormPage />
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <HelpDeskDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ticket"
        element={
          <ProtectedRoute>
            <Layout>
              <TicketDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-members"
        element={
          <ProtectedRoute>
            {authLoading ? (
              <Layout>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 2 }}>
                  <CircularProgress sx={{ color: '#845EC2' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)' }}>กำลังโหลด...</Typography>
                </Box>
              </Layout>
            ) : currentUser?.is_staff ? (
              <Layout>
                <MembersManagementPage />
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-members/create"
        element={
          <ProtectedRoute>
            {authLoading ? (
              <Layout>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 2 }}>
                  <CircularProgress sx={{ color: '#845EC2' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)' }}>กำลังโหลด...</Typography>
                </Box>
              </Layout>
            ) : currentUser?.is_staff ? (
              <Layout>
                <MemberFormPage />
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-members/edit/:id"
        element={
          <ProtectedRoute>
            {authLoading ? (
              <Layout>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 2 }}>
                  <CircularProgress sx={{ color: '#845EC2' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)' }}>กำลังโหลด...</Typography>
                </Box>
              </Layout>
            ) : currentUser?.is_staff ? (
              <Layout>
                <MemberFormPage />
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
        </Router>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
